import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { TrendingUp, TrendingDown, ArrowRight, Calculator, RefreshCw } from "lucide-react";

export default function ScenarioPage() {
  const { activeEntity, entities, categories } = useApp();
  const [type, setType] = useState("EXTEND_LIFE");
  const [target, setTarget] = useState({ entity_id: "", category_id: "", asset_id: "" });
  const [params, setParams] = useState({ new_life_months: 120, new_residual: 0, recoverable_amount: 0 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/scenarios/simulate", { type, target, ...params });
      setResult(data);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="scenario-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
          <Calculator size={24} className="inline mr-2 text-[#C8A961]" />
          What-If Scenario Modeller
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">Model the P&L and balance sheet impact of proposed changes <strong>before</strong> committing. All changes apply prospectively under IAS 8.36.</p>
      </div>

      <div className="stratum-card p-6">
        <h3 className="font-display text-base font-semibold mb-4">Configure Scenario</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Scenario Type</label>
            <select className="stratum-input w-full" value={type} onChange={e => setType(e.target.value)} data-testid="scenario-type">
              <option value="EXTEND_LIFE">Extend Useful Life</option>
              <option value="CHANGE_RESIDUAL">Change Residual Value</option>
              <option value="IMPAIR">Test Impairment Impact</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Entity (or all)</label>
            <select className="stratum-input w-full" value={target.entity_id} onChange={e => setTarget({...target, entity_id: e.target.value})}>
              <option value="">All entities</option>
              {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6B7280] mb-1">Category (or all)</label>
            <select className="stratum-input w-full" value={target.category_id} onChange={e => setTarget({...target, category_id: e.target.value})}>
              <option value="">All categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {type === "EXTEND_LIFE" && (
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">New Useful Life (months)</label>
              <input type="number" className="stratum-input w-full font-mono" value={params.new_life_months} onChange={e => setParams({...params, new_life_months: parseInt(e.target.value) || 0})} />
            </div>
          )}
          {type === "CHANGE_RESIDUAL" && (
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">New Residual Value (R)</label>
              <input type="number" className="stratum-input w-full font-mono" value={params.new_residual} onChange={e => setParams({...params, new_residual: parseFloat(e.target.value) || 0})} />
            </div>
          )}
          {type === "IMPAIR" && (
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Recoverable Amount (R)</label>
              <input type="number" className="stratum-input w-full font-mono" value={params.recoverable_amount} onChange={e => setParams({...params, recoverable_amount: parseFloat(e.target.value) || 0})} />
            </div>
          )}
        </div>
        <button onClick={simulate} disabled={loading} className="stratum-btn-primary flex items-center gap-2" data-testid="run-scenario-btn">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Calculating..." : "Run Scenario"}
        </button>
      </div>

      {result && (
        <>
          <div className={`stratum-card p-6 ${result.impact.annual_dep_change < 0 ? "border-l-4 border-l-[#065F46]" : "border-l-4 border-l-[#92400E]"}`}>
            <h3 className="font-display text-lg font-semibold text-[#0B1020] mb-4">Scenario Result</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              <div className="p-4 rounded-lg bg-[#FAF8F4]">
                <div className="text-xs text-[#6B7280] mb-1">Current Annual Depreciation</div>
                <div className="font-mono text-xl font-semibold tabular-nums">{formatCurrency(result.impact.current_annual_dep)}</div>
              </div>
              <div className="p-4 rounded-lg bg-[#FAF8F4]">
                <div className="text-xs text-[#6B7280] mb-1">Proposed Annual Depreciation</div>
                <div className="font-mono text-xl font-semibold tabular-nums text-[#C8A961]">{formatCurrency(result.impact.proposed_annual_dep)}</div>
              </div>
              <div className={`p-4 rounded-lg ${result.impact.annual_dep_change < 0 ? "bg-[#065F46]/10" : "bg-[#92400E]/10"}`}>
                <div className="text-xs text-[#6B7280] mb-1">P&L Impact</div>
                <div className="font-mono text-xl font-semibold tabular-nums">{formatCurrency(Math.abs(result.impact.annual_dep_change))}</div>
                <div className="text-xs mt-1">{result.impact.pl_impact}</div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[#1E40AF]/5 border border-[#1E40AF]/20 text-sm">
              <strong className="text-[#1E40AF]">IAS 8.36:</strong> <span className="text-[#6B7280]">{result.legislation.note}</span>
            </div>
          </div>

          <div className="stratum-card overflow-hidden">
            <div className="p-4 border-b border-[#E8E2D5]"><h4 className="font-display text-sm font-semibold">{result.assets_affected} assets affected</h4></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs stratum-table">
                <thead><tr><th>Asset</th><th className="text-right">Current Monthly</th><th className="text-right">Proposed Monthly</th><th className="text-right">Current Annual</th><th className="text-right">Proposed Annual</th><th>Impact Note</th></tr></thead>
                <tbody>
                  {result.details.map(d => (
                    <tr key={d.asset_id}>
                      <td><span className="font-mono">{d.tag}</span> <span className="text-[#6B7280]">{d.description?.substring(0, 30)}</span></td>
                      <td className="text-right font-mono tabular-nums">{formatCurrency(d.current_monthly)}</td>
                      <td className="text-right font-mono tabular-nums text-[#C8A961]">{formatCurrency(d.proposed_monthly)}</td>
                      <td className="text-right font-mono tabular-nums">{formatCurrency(d.current_annual)}</td>
                      <td className="text-right font-mono tabular-nums text-[#C8A961]">{formatCurrency(d.proposed_annual)}</td>
                      <td className="text-[#6B7280] max-w-[200px] truncate">{d.explanation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
